import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Send, Terminal, Wand2 } from "lucide-react";

const SCOPES = [
  { key: "all", label: "All" },
  { key: "selected", label: "Selected" },
  { key: "files", label: "Files" },
  { key: "folders", label: "Folders" },
];

const PRESETS = [
  { key: "extract_names", label: "Extract Names" },
  { key: "clean_underscores", label: "Clean Separators" },
  { key: "remove_tags", label: "Remove Tags" },
  { key: "title_case", label: "Title Case" },
  { key: "snake_case", label: "snake_case" },
  { key: "kebab_case", label: "kebab-case" },
];

const QUICK = [
  "Extract only the person's names",
  "Rename each folder to the person's name in its files",
  "Remove all ID numbers and _watermarked",
  "Make everything Title Case",
];

export default function ChatAssistant({ messages, onSend, onPreset, busy, hasItems, scope, setScope }) {
  const [input, setInput] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = (text) => {
    const value = (text ?? input).trim();
    if (!value || busy) return;
    onSend(value);
    setInput("");
  };

  return (
    <div className="scanlines relative flex flex-col h-full bg-[#050505]/95 border-r border-white/10 z-10">
      <div className="relative z-[2] px-5 py-4 border-b border-white/10 bg-[#050505] flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-[#00E5FF] ai-glow" />
        </div>
        <div>
          <h2 className="font-display font-black text-white text-base leading-none">AI Assistant</h2>
          <p className="text-[11px] text-neutral-500 mt-1 font-mono-ui flex items-center gap-1">
            <Terminal className="h-3 w-3" /> describe how to rename
          </p>
        </div>
      </div>

      {/* scope */}
      <div className="relative z-[2] px-4 pt-3 pb-2 border-b border-white/8">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">AI works on</div>
        <div className="flex flex-wrap gap-1.5">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              data-testid={`ai-scope-${s.key}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors duration-200 ${
                scope === s.key ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "border-white/15 text-neutral-400 hover:border-[#00E5FF]/50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* presets */}
      <div className="relative z-[2] px-4 py-3 border-b border-white/8">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 flex items-center gap-1"><Wand2 className="h-3 w-3 text-[#00E5FF]" /> Presets</div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => onPreset(p.key)}
              disabled={busy || !hasItems}
              data-testid={`preset-${p.key}`}
              className="text-[11px] font-mono-ui px-2.5 py-1 rounded-full border border-white/15 text-neutral-300 hover:border-[#00E5FF]/50 hover:text-[#00E5FF] transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative z-[2] flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-sm text-neutral-400 leading-relaxed font-mono-ui">
            <span className="text-[#00E5FF]">›</span> Tell me what to do in plain English. I understand the whole folder tree and follow-up instructions.
          </div>
        )}
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[85%] text-sm leading-relaxed rounded-2xl px-4 py-2.5 ${m.role === "user" ? "bg-white/10 text-white rounded-br-sm" : "bg-[#00E5FF]/10 border border-[#00E5FF]/25 text-[#dffaff] rounded-bl-sm font-mono-ui"}`}>
              {m.content}
            </div>
          </motion.div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-[#00E5FF]/10 border border-[#00E5FF]/25 rounded-2xl rounded-bl-sm px-4 py-2.5 text-[#00E5FF] font-mono-ui text-sm">
              thinking<span className="blink-cursor" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="relative z-[2] px-4 pb-3 flex flex-wrap gap-2">
        {QUICK.map((q) => (
          <button key={q} onClick={() => send(q)} disabled={busy || !hasItems} data-testid={`quick-chip-${q.slice(0, 10)}`} className="text-[11px] font-mono-ui px-2.5 py-1 rounded-full border border-white/15 text-neutral-400 hover:border-[#00E5FF]/50 hover:text-[#00E5FF] transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
            {q}
          </button>
        ))}
      </div>

      <div className="relative z-[2] p-4 border-t border-white/10 bg-[#050505]">
        <div className="flex items-end gap-2 bg-black/60 border border-white/15 rounded-2xl px-3 py-2 focus-within:border-[#00E5FF]/50 transition-colors duration-200">
          <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={hasItems ? "Describe your rename…" : "Import files first…"}
            data-testid="ai-chat-input"
            className="flex-1 bg-transparent resize-none outline-none text-sm text-white placeholder:text-neutral-600 font-mono-ui max-h-28"
          />
          <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="ai-chat-send" className="h-8 w-8 shrink-0 rounded-xl bg-[#00E5FF] text-black flex items-center justify-center hover:bg-[#00B3CC] hover:scale-105 transition-all duration-200 disabled:opacity-40 disabled:hover:scale-100">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
